import React, {
  useRef,
  useState,
  useMemo,
  forwardRef,
  useCallback,
  useEffect,
} from "react";
import {
  isEmpty,
  forEach,
  split,
  map,
  isString,
  isNull,
  omitBy,
  isEqual,
  uniqBy,
  some,
  reduce,
} from "utils/lodashUtils";
import { Form, Button } from "antd";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, generateFormFields } from "utils/formUtils";
import GenericMappingForm from "../GenericMappingForm";
import message from "components/CustomMessage";
import { parseSelectOptions } from "utils/formUtils";

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  projectList: [{ key: undefined, value: undefined }],
};

const SentinelOneMappingForm = forwardRef(function SentinelOneMappingForm(
  { disableProjectFields = false, ...props },
  ref
) {
  const [form] = Form.useForm();
  const [mappedEndPointList] = useState(() => {
    if (props.record && !isNull(props.record?.endpoints)) {
      const { endpoints } = props.record;
      return {
        projects: split(endpoints, ",").map((i) => ({ id: i, name: i })),
      };
    }
    return { projects: [] };
  });
  const endpointRef = useRef({});
  const [tagKeys, setTagKeys] = useState([]);
  const [valueOptions, setValueOptions] = useState({});

  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [{ ...initialFormValues }];
  });

  useEffect(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        login_id: props.loginConfigId,
        type: "tags",
      });
    promise
      .then((response) => {
        const { projects } = response;
        setTagKeys(projects);
      })
      .catch(() => {
        message.error("Problem fetching tag keys");
      });

    return cancel;
  }, [props.loginConfigId, props.toolName]);

  const parseExtraFieldsDataFromResponse = useCallback(
    (config) => {
      const { endpoints, tags } = config;
      const parsedFields = {};
      if (isNull(endpoints)) {
        parsedFields.endpointsCheck = true;
      } else {
        parsedFields.endpoint = split(endpoints, ",");
      }
      if (tags) {
        parsedFields.projectList = Object.keys(JSON.parse(tags)).map((key) => {
          return {
            key: key,
            value: JSON.parse(tags)[key],
          };
        });

        let valueObj = {};

        Object.keys(JSON.parse(tags)).forEach((key) => {
          valueObj[key] = tagKeys.find(
            (tagKey) => tagKey.id === key
          )?.otherProperties?.values;
        });

        setValueOptions((values) => ({
          ...values,
          ...valueObj,
        }));
      }
      return parsedFields;
    },
    [tagKeys]
  );

  const getExtraFieldsData = useCallback((config) => {
    const { endpointsCheck, endpoint, projectList } = config;
    if (isEmpty(endpoint)) {
      message.error("Please select atleast one Endpoint");
      throw new Error("Please select atleast one Endpoint");
    }
    const extraFieldData = {};

    if (endpointsCheck) {
      extraFieldData.endpoints = null;
    } else {
      extraFieldData.endpoints = endpoint.join();
    }
    if (!isEmpty(projectList)) {
      const tags = {};
      projectList.forEach((item) => {
        tags[item.key] = item.value;
      });
      extraFieldData.tags = isEmpty(omitBy(tags, isEmpty))
        ? null
        : JSON.stringify(tags);
    } else {
      extraFieldData.tags = null;
    }
    return extraFieldData;
  }, []);

  const onKeyChange = useCallback(
    (project, field, listField) => {
      const { name } = field;
      const configurations = form.getFieldValue("configurations");
      const currentKey = configurations[listField.name]?.projectList[name]?.key;
      setValueOptions((values) => ({
        ...values,
        [currentKey]: tagKeys.find((p) => p.id === currentKey)?.otherProperties
          ?.values,
      }));
    },
    [form, tagKeys]
  );

  const fetchEndpoints = useCallback(
    (params) => {
      const { promise, cancel } =
        ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
          login_id: props.loginConfigId,
          type: "endpoints",
          ...params,
        });
      promise.then((response) => {
        let { projects } = response;
        if (mappedEndPointList["projects"].length > 0 && !isEmpty(projects)) {
          response.projects = uniqBy(
            [...projects, ...mappedEndPointList["projects"]],
            "id"
          );
        }
      });
      return { promise, cancel };
    },
    [props.toolName, props.loginConfigId, mappedEndPointList]
  );

  const projectListRender = useCallback(
    (listField) => {
      const __fields = [
        {
          label: "Tag key",
          type: FieldTypes.SELECT,
          options: tagKeys,
          colSpan: 10,
          name: "key",
          disabled: disableProjectFields,
          props: {
            autoFocus: !disableProjectFields,
            onChange: (project, field) =>
              onKeyChange(project, field, listField),
          },
        },
        {
          name: "value",
          label: "Tag Value",
          type: FieldTypes.MULTI_SELECT,
          colSpan: 10,
          disabled: disableProjectFields,
          props: {
            getFormListOptions: (nestedListField) => {
              const configs = form.getFieldValue("configurations");
              const currentTagKey =
                configs[listField.name]?.projectList[nestedListField]?.key;
              return parseSelectOptions(valueOptions[currentTagKey]);
            },
            setFormListItemProps: (field, nestedListField) => {
              const configs = form.getFieldValue("configurations");
              const currentTagKey =
                configs[listField.name]?.projectList[nestedListField.name]?.key;
              return {
                rules: [
                  {
                    required: currentTagKey,
                    message: "Tag value is required, if tag key is selected",
                  },
                ],
              };
            },
          },
        },
        {
          name: "add",
          colSpan: 2,
          render: ({ remove, name, fields }) => {
            return (
              <Button
                disabled={fields.length === 1}
                type="dashed"
                className="m-t-l"
                onClick={() => {
                  remove(name);
                }}
              >
                -
              </Button>
            );
          },
        },
      ];

      return (
        <div className="m-b-sm">
          <Form.List name={[listField.name, "projectList"]}>
            {(fields, { add, remove }) => {
              return (
                <div>
                  {map(fields, (field) => {
                    return generateFormFields(
                      __fields,
                      10,
                      { ...field, add, remove, fields },
                      field.name
                    );
                  })}
                  <Button
                    size="small"
                    type="dashed"
                    onClick={() => add({ key: undefined, value: undefined })}
                    disabled={disableProjectFields}
                  >
                    + Add Tags
                  </Button>
                </div>
              );
            }}
          </Form.List>
        </div>
      );
    },
    [form, onKeyChange, tagKeys, valueOptions, disableProjectFields]
  );

  const extraFields = useMemo(
    () => [
      {
        name: (listField) => [listField.name, "projectList"],
        render: projectListRender,
      },
      {
        name: "endpoint",
        colSpan: "10",
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: <span className="required-mark">Endpoints</span>,
        placeholder: "Endpoints",
        props: {
          mode: "multiple",
          setExtraProps: (field, listField) => {
            const allConfigs = form.getFieldsValue().configurations;
            const endPointChecked = allConfigs[listField.name]?.endpointsCheck;
            let project = undefined;
            const projectList = allConfigs[listField.name]?.projectList;

            const isEmptyProjectList = some(
              projectList,
              (obj) => isEmpty(obj.key) || isEmpty(obj.value)
            );

            if (projectList && !isEmptyProjectList) {
              project = reduce(
                projectList,
                (acc, { key, value }) => {
                  acc[key] = (acc[key] || []).concat(value);
                  return acc;
                },
                {}
              );
            }
            const uniqueKey = `${listField.name}`;
            if (
              (project &&
                !isEqual(
                  JSON.stringify(project),
                  endpointRef.current[uniqueKey]?.project
                )) ||
              !endpointRef.current[uniqueKey]
            ) {
              endpointRef.current[uniqueKey] = {
                project: JSON.stringify(project),
              };
            }
            return {
              api: fetchEndpoints,
              apiParams: endpointRef.current[uniqueKey],
              resetOptionsProp: JSON.stringify(project),
              mode: "multiple",
              allowClear: true,
              disabled: endPointChecked,
            };
          },
        },
      },
      // {
      //   name: "endpointsCheck",
      //   colSpan: "10",
      //   type: FieldTypes.BOOLEAN,
      //   inline: true,
      //   label: "Select All Endpoints",
      //   className: "m-t-l",
      // },
    ],
    [projectListRender, fetchEndpoints, form]
  );

  const onValuesChange = useCallback(
    (values, ...args) => {
      if (values.configurations) {
        let isProjectChanged = false;
        const formValues = form.getFieldsValue();
        forEach(values.configurations, (configs, index) => {
          //look into SnykMappingFormV2 for explanation
          if (
            !configs ||
            !configs.projectList ||
            isString(configs.projectList)
          ) {
            return;
          }
          forEach(configs.projectList, (object, i) => {
            //look into SnykMappingFormV2 for explanation
            if (object && object.key && Object.keys(object).length === 1) {
              isProjectChanged = true;
              formValues.configurations[index].projectList[i].value = undefined;
              formValues.configurations[index].endpoint = undefined;
            }
          });
        });
        if (isProjectChanged) {
          form.setFieldsValue(formValues);
        }
      }
    },
    [form]
  );

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      onValuesChange={onValuesChange}
      {...props}
    />
  );
});

export default SentinelOneMappingForm;
